import { createSelector } from '@ngrx/store';
import { Fee, Metadata } from 'libs/datalayer/src/lib/apimodels/draft/metadata';
import { CategoryEnum } from 'libs/datalayer/src/lib/enums/category';
import { getListingPrice, getPackagePrice } from '../../helpers/price.helper';
import { DraftPartialState, DRAFT_FEATURE_KEY } from '@classifieds-sell/state';
import {
    RouterPartialState,
    RouterState,
    ROUTER_FEATURE_KEY
} from '@classifieds-sell/state';

export interface FeeCalculationState
    extends DraftPartialState,
    RouterPartialState { }

const draft = (state: FeeCalculationState) => state[DRAFT_FEATURE_KEY];
const router = (state: FeeCalculationState) => state[ROUTER_FEATURE_KEY];

const draftStateSelector = createSelector(draft, (d) => d);
const routerStateSelector = createSelector(router, (r) => r);

function isEditOrRelist(routerState: RouterState) {
    return routerState.data.editing || routerState.data.relisting;
}

function packagePricesByName(name: string, feeTable: Fee[]) {
    return feeTable.find((f) => f.name.toLowerCase() === name);
}

function feeDeterminer(
    category: CategoryEnum | undefined,
    draftData: any,
    metadata: Metadata,
    effectiveFeeDeterminer: number | undefined,
    isAgent: boolean | undefined
): number {
    if (isAgent) {
        return undefined;
    }
    const listingPrice = getListingPrice(category, draftData, metadata);
    if (!effectiveFeeDeterminer) {
        return listingPrice;
    }
    return effectiveFeeDeterminer > listingPrice
        ? effectiveFeeDeterminer
        : listingPrice;
}

const listingFeeTable = createSelector(draftStateSelector, (draftState) => {
    return draftState.metadata.fees;
});

const currentListingPackageInformation = createSelector(
    draftStateSelector,
    (draftState) => {
        return draftState.metadata.privateSellerPackageInformation;
    }
);

const disabledPackagesForSelection = createSelector(
    currentListingPackageInformation,
    listingFeeTable,
    routerStateSelector,
    (packageInformation, feeTable, routerState) => {
        const isEditOrRelistPage = isEditOrRelist(routerState);
        if (routerState.data.agent && isEditOrRelistPage) {
            return undefined;
        }
        if (!isEditOrRelistPage) {
            return [];
        }
        return feeTable
            .filter((fee) => fee.id < packageInformation.listingPackageId)
            .map((fee) => fee.id);
    }
);

const listingFeeDeterminer = createSelector(
    draftStateSelector,
    routerStateSelector,
    (draftState, routerState) => {
        const draftData = draftState.draftData;
        const effectiveFeeDeterminer =
            draftState.metadata.privateSellerPackageInformation
                ?.effectiveFeeDeterminer;
        const categoryId = routerState.data.categoryId;
        return feeDeterminer(
            categoryId,
            draftData,
            draftState.metadata,
            effectiveFeeDeterminer,
            false
        );
    }
);

// start exported members

/**
 * selector used to render the card on property promote page for each card
 */
export const packageFees = createSelector(
    listingFeeDeterminer,
    currentListingPackageInformation,
    listingFeeTable,
    disabledPackagesForSelection,
    routerStateSelector,
    (currentfeeDeterminer, info, feeTable, disabledPackages, routerState) => {
        const isEditOrRelistPage = isEditOrRelist(routerState);
        if (routerState.data.agent && isEditOrRelistPage) {
            return undefined;
        }
        return feeTable.map((fee) => {
            const packagePrice = getPackagePrice(
                fee.prices,
                currentfeeDeterminer
            );
            function isDisabledPackage() {
                return disabledPackages.some((p) => p === fee.id);
            }
            function priceForEditOrRelist() {
                if (isDisabledPackage()) {
                    return null; // null will not render price
                }
                const paidAmount = getPackagePrice(
                    feeTable.find((f) => f.id === info.listingPackageId).prices,
                    info.effectiveFeeDeterminer
                );
                return packagePrice - paidAmount;
            }
            return {
                ...fee,
                packagePrice: !isEditOrRelistPage
                    ? packagePrice
                    : priceForEditOrRelist(),
                isDisabled: isDisabledPackage()
            };
        });
    }
);
/**
 * selector used to render the super feature repurchase section
 */
export const superFeatureRepurchaseInfo = createSelector(
    listingFeeTable,
    currentListingPackageInformation,
    listingFeeDeterminer,
    routerStateSelector,
    draftStateSelector,
    (feeTable, info, currentFeeDeterminer, routerState, draftState) => {
        const isEditOrRelistPage = isEditOrRelist(routerState);
        if (routerState.data.agent && isEditOrRelistPage) {
            return undefined;
        }
        // hasSuperFeature is tri state true, false, null means N/A e.g. for sliver and bronze
        const goldPackage = packagePricesByName('gold', feeTable);
        const silverPackage = packagePricesByName('silver', feeTable);
        const hasSuperFeature =
            draftState.originalDraftData.propertyPromote.hasSuperFeature;

        const isSuperFeatureExpired =
            isEditOrRelistPage && // only show on edit of relist page
            info?.listingPackageId === goldPackage.id && // listing is a gold listing
            hasSuperFeature === false; // gold listing has not super feature meaning it is expired
        return {
            isSuperFeatureExpired: isSuperFeatureExpired,
            superFeatureRepurchaseFee: isSuperFeatureExpired
                ? getPackagePrice(goldPackage.prices, currentFeeDeterminer) -
                getPackagePrice(silverPackage.prices, currentFeeDeterminer)
                : undefined
        };
    }
);
/**
 * selector used in property information page to show the popup message
 */
export const isIncreasePricingTier = createSelector(
    listingFeeDeterminer,
    currentListingPackageInformation,
    routerStateSelector,
    (currentFeeDeterminer, info, routerState) => {
        const isEditOrRelistPage = isEditOrRelist(routerState);
        if (routerState.data.agent && isEditOrRelistPage) {
            return undefined;
        }
        return (
            isEditOrRelistPage &&
            currentFeeDeterminer > info.effectiveFeeDeterminer
        );
    }
);

/**
 * selector used in property sumary page to show the total price
 */
export const totalFee = createSelector(
    draftStateSelector,
    packageFees,
    superFeatureRepurchaseInfo,
    (draftState, calculatedFees, superFeatureInfo) => {
        const intendedPackageId =
            draftState.draftData.propertyPromote.categoryPackageId;
        if (!intendedPackageId) {
            return 0;
        }
        const hasSuperFeature =
            draftState.draftData.propertyPromote.hasSuperFeature;
        const categoryFees = calculatedFees.find(
            (fee) => fee.id === intendedPackageId
        );
        const listingFees = categoryFees.packagePrice;
        if (listingFees === null) {
            // user some how manage to downgrade package
            return null;
        }
        if (
            superFeatureInfo.isSuperFeatureExpired &&
            hasSuperFeature === true
        ) {
            return listingFees + superFeatureInfo.superFeatureRepurchaseFee;
        }
        return listingFees;
    }
);
